import { useCallback, useEffect, useRef, useState } from 'react';

/** Long enough to swallow a fast typist's keystrokes, short enough that Save never feels stale. */
export const TEXT_COMMIT_DELAY_MS = 150;

/** Commit on blur only, for an owner whose re-render is too expensive to do mid-typing. */
export const COMMIT_ON_BLUR = null;

export interface TypedText {
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** Commits immediately, so leaving the field never loses the last few characters. */
  readonly onBlur: () => void;
  /** Enter and Escape both commit, then run their action; any other key is ignored. */
  readonly onKey: (key: string, actions?: TypedTextKeyActions) => void;
}

export interface TypedTextKeyActions {
  readonly onEnter?: () => void;
  readonly onEscape?: () => void;
}

/**
 * Keeps keystrokes local and tells the owner once typing settles: the Studio's draft is one object,
 * so committing per keystroke re-renders every consumer. Debounced for React, not the network.
 */
export const useTypedText = (
  value: string,
  commit: (next: string) => void,
  delayMs: number | null = TEXT_COMMIT_DELAY_MS,
  commitOnEmptyFlip = false
): TypedText => {
  const [typed, setTyped] = useState<string | null>(null);
  // The pending text, read by `onBlur` without making it depend on a render.
  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Re-reading the latest commit keeps `onChange` stable even though callers pass a new closure.
  const onCommit = useRef(commit);
  onCommit.current = commit;
  const committed = useRef(value);
  committed.current = value;

  // React fires no blur on unmount, so flush the pending edit or the last keystrokes are lost.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (pending.current !== null) onCommit.current(pending.current);
    },
    []
  );

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const next = pending.current;
    if (next === null) return;
    pending.current = null;
    setTyped(null);
    onCommit.current(next);
  }, []);

  const onChange = useCallback(
    (next: string) => {
      pending.current = next;
      setTyped(next);
      clearTimeout(timer.current);
      // Filling or clearing the field commits at once, so a Save gated on it is right before the blur.
      if (commitOnEmptyFlip && Boolean(next.trim()) !== Boolean(committed.current.trim())) {
        flush();
        return;
      }
      // `null` commits on blur only, for an owner whose commit re-renders too much to do mid-typing.
      if (delayMs !== null) {
        timer.current = setTimeout(flush, delayMs);
      }
    },
    [delayMs, flush, commitOnEmptyFlip]
  );

  const onKey = useCallback(
    (key: string, actions?: TypedTextKeyActions) => {
      if (key !== 'Enter' && key !== 'Escape') return;
      flush();
      if (key === 'Enter') actions?.onEnter?.();
      else actions?.onEscape?.();
    },
    [flush]
  );

  // No effect syncs `value` back in: nothing is pending exactly when `typed` is null, and then the
  // owner's value is what renders.
  return { value: typed ?? value, onChange, onBlur: flush, onKey };
};
